import React from 'react';
import { Box, Typography, Paper, Chip, Avatar, Tooltip, useTheme, alpha } from '@mui/material';
import { stringToColor, getInitials } from '../helpers';

export const StatCard = ({ icon, value, label, sublabel, color }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 3,
      border: `1px solid ${alpha(color, 0.18)}`,
      overflow: 'hidden',
      height: '100%',
      width: '100%',
      transition: 'transform 0.18s, box-shadow 0.18s',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: `0 8px 28px ${alpha(color, 0.22)}`,
      },
    }}
  >
    <Box sx={{ height: 4, bgcolor: color }} />
    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, height: 'calc(100% - 4px)' }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: alpha(color, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box minWidth={0} flex={1}>
        <Typography
          fontWeight={800}
          color={color}
          lineHeight={1.1}
          sx={{ mb: 0.4, fontSize: { xs: '1.1rem', md: '1.3rem' }, wordBreak: 'break-word' }}
        >
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ fontSize: 11 }}>
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
  </Paper>
);

export const StatusBadge = ({ label, color }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 0.3,
      borderRadius: 1.5,
      bgcolor: alpha(color, 0.1),
      border: `1px solid ${alpha(color, 0.25)}`,
      flexShrink: 0,
    }}
  >
    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
    <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, color, lineHeight: 1 }}>
      {label}
    </Typography>
  </Box>
);

export const PersonChip = ({ user, label }) => {
  if (!user) return null;
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  if (!name) return null;
  const color = stringToColor(name);
  return (
    <Tooltip title={`${label}: ${name}`}>
      <Chip
       onClick={() => {
                    }}
        size="small"
        avatar={
          <Avatar
            sx={{
              bgcolor: `${color} !important`,
              width: '20px !important',
              height: '20px !important',
              fontSize: '9px !important',
              fontWeight: 700,
            }}
          >
            {getInitials(user.first_name, user.last_name)}
          </Avatar>
        }
        label={name}
        sx={{
          height: 24,
          fontSize: 11,
          fontWeight: 500,
          bgcolor: alpha(color, 0.08),
          border: `1px solid ${alpha(color, 0.2)}`,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    </Tooltip>
  );
};

export const EmptyState = ({ icon, text }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.text.disabled, 0.07),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
          opacity: 0.5,
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" color="text.disabled" fontWeight={500}>
        {text}
      </Typography>
    </Box>
  );
};
